import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { authApi, type AuthUser } from "@/api/auth.api";
import { setAccessToken } from "@/api/http";
import { normalizeRole } from "@/shared/role";

const SESSION_HINT_KEY = "tm_refresh_token";

function setSessionHint(token: string | null) {
  try {
    if (token) sessionStorage.setItem(SESSION_HINT_KEY, token);
    else sessionStorage.removeItem(SESSION_HINT_KEY);
  } catch {
    /* sessionStorage unavailable (private mode); ignore */
  }
}

function getSessionHint(): string | null {
  try {
    return sessionStorage.getItem(SESSION_HINT_KEY);
  } catch {
    return null;
  }
}

export const useAuthStore = defineStore("auth", () => {
  const user = ref<AuthUser | null>(null);
  const token = ref<string | null>(null);
  const ready = ref(false);

  const isAuthenticated = computed(() => !!user.value);
  const role = computed(() =>
    user.value ? normalizeRole(user.value.role) : null,
  );
  // hospId from the user's JWT claim. 0 = SUPERADMIN (cross-hospital), >0 = own hospital.
  const hospId = computed(() => user.value?.hospId ?? 1);
  // Hospital branch name returned by the login/refresh response. Null for SUPERADMIN.
  const hospName = computed(() => user.value?.hospName ?? null);

  async function login(username: string, password: string) {
    const r = await authApi.login(username, password);
    token.value = r.accessToken;
    user.value = r.user;
    setAccessToken(r.accessToken);
    setSessionHint(r.refreshToken ?? null);
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    await authApi.changePassword(currentPassword, newPassword);
    // Backend invalidates refresh cookie + revokes refresh tokens. Force re-login.
    token.value = null;
    user.value = null;
    setAccessToken(null);
    setSessionHint(null);
  }

  async function bootstrap() {
    if (ready.value) return;
    const rt = getSessionHint();
    if (!rt) {
      // No prior session on this tab — skip refresh to avoid 401 console noise.
      ready.value = true;
      return;
    }
    try {
      const r = await authApi.refresh(rt);
      token.value = r.accessToken;
      user.value = r.user;
      setAccessToken(r.accessToken);
      if (r.refreshToken) setSessionHint(r.refreshToken);
    } catch {
      token.value = null;
      user.value = null;
      setAccessToken(null);
      setSessionHint(null);
    } finally {
      ready.value = true;
    }
  }

  async function logout() {
    try {
      const rt = getSessionHint();
      await authApi.logout(rt ?? undefined);
    } catch {
      /* ignore */
    }
    token.value = null;
    user.value = null;
    setAccessToken(null);
    setSessionHint(null);
  }

  return {
    user,
    token,
    ready,
    isAuthenticated,
    role,
    hospId,
    hospName,
    login,
    bootstrap,
    logout,
    changePassword,
  };
});
